import React, { useState, useEffect } from 'react';
import { useLanguage } from '../../context/LanguageContext';
import { track } from '@vercel/analytics';
import './FloatingCoursesButton.css';

const FloatingCoursesButton: React.FC = () => {
  const { language } = useLanguage();
  const [isEnhanced, setIsEnhanced] = useState(false);
  const [hasScrolled, setHasScrolled] = useState(false);

  useEffect(() => {
    let scrollTimeout: NodeJS.Timeout;
    let enhanceTimeout: NodeJS.Timeout;

    const handleScroll = () => {
      const scrollY = window.scrollY;
      const windowHeight = window.innerHeight;

      // Mark that user has scrolled (for slight style changes)
      if (scrollY > 50 && !hasScrolled) {
        setHasScrolled(true);
      }

      // Enhanced animation on longer scrolls for extra attention
      if (scrollY > windowHeight / 3) {
        setIsEnhanced(true);

        // Clear existing timeout
        if (enhanceTimeout) {
          clearTimeout(enhanceTimeout);
        }

        // Remove enhanced state after 4 seconds of no scrolling
        enhanceTimeout = setTimeout(() => {
          setIsEnhanced(false);
        }, 4000);
      } else {
        setIsEnhanced(false);
      }

      // Debounce scroll events
      if (scrollTimeout) {
        clearTimeout(scrollTimeout);
      }

      scrollTimeout = setTimeout(() => {
        // Additional scroll-based logic can go here
      }, 100);
    };

    // Add scroll listener with passive option for better performance
    window.addEventListener('scroll', handleScroll, { passive: true });

    // Initial check
    handleScroll();

    return () => {
      window.removeEventListener('scroll', handleScroll);
      if (scrollTimeout) clearTimeout(scrollTimeout);
      if (enhanceTimeout) clearTimeout(enhanceTimeout);
    };
  }, [hasScrolled]);

  const handleButtonClick = (e: React.MouseEvent) => {
    e.preventDefault();

    // Track the click event
    track('courses_button_click', {
      language,
      page_location: window.location.pathname,
      button_position: 'floating_overlay',
      scroll_position: window.scrollY,
      has_scrolled: hasScrolled,
      is_enhanced: isEnhanced,
      timestamp: new Date().toISOString(),
      user_agent: navigator.userAgent,
      screen_width: window.innerWidth
    });

    // Navigate to courses
    window.open('https://www.aiaj.xyz/training/index.html', '_blank', 'noopener,noreferrer');
  };

  // Graduation cap / courses icon component
  const CoursesIcon = () => (
    <svg
      className="courses-icon"
      viewBox="0 0 24 24"
      fill="currentColor"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path d="M12 3L1 9l4 2.18v6L12 21l7-3.82v-6l2-1.09V17h2V9L12 3zm6.82 6L12 12.72 5.18 9 12 5.28 18.82 9zM17 15.99l-5 2.73-5-2.73v-3.72L12 15l5-2.73v3.72z"/>
    </svg>
  );

  return (
    <div className={`floating-courses-button visible ${hasScrolled ? 'scrolled' : ''} ${isEnhanced ? 'enhanced' : ''}`}>
      <button
        className="courses-button"
        onClick={handleButtonClick}
        aria-label={language === 'ar' ? 'الدورات التدريبية - يفتح في نافذة جديدة' : 'Training Courses - Opens in new window'}
      >
        <CoursesIcon />

        <div className="courses-text">
          <span className="courses-title">
            {language === 'ar' ? 'مركز التدريب' : 'Training Center'}
          </span>
          <span className="courses-subtitle">
            {language === 'ar' ? 'ابدأ التعلم' : 'Start Learning'}
          </span>
        </div>

        {/* Pulse indicator for attention */}
        <div className="pulse-indicator"></div>
      </button>
    </div>
  );
};

export default FloatingCoursesButton;